import { StringHelpers } from "../helpers/string-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { SupportResistanceLineModel } from "../models/support-resistance-line/support-resistance-line.model";
import { IComponent } from "./component.interface";

export class SupportResistanceLineComponent implements IComponent {
    public id = StringHelpers.newGuid();

    private base: IChartContainer;

    private pinElement: HTMLElement | null = null;
    private annotations: Array<SupportResistanceLineModel> = [];
    private item: SupportResistanceLineModel | null = null;
    private isRemovingForSwitchingSymbol = false;

    constructor(base: IChartContainer) {
        this.base = base;
        this.subscribe();
        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            setTimeout(() => {
                this.setPinAndPosition();
            }, 50);
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            setTimeout(() => {
                this.setPinAndPosition();
            }, 50);
        });
        this.base.tiChartSeries?.subscribeChartLoad(this.id, () => {
            setTimeout(() => {
                this.create();
            }, 500);
        });
    }

    public remove() {
        this.isRemovingForSwitchingSymbol = true;
        this.removeAnnotations();
        this.removePin();

        this.isRemovingForSwitchingSymbol = false;
    }

    private removeAnnotations() {
        for (const item of this.annotations) {
            try {
                this.base.tiChartSeries?.highStockChart?.removeAnnotation(item.id);
            } catch { }
        }

        this.annotations = [];
    }

    private chartStyle: ChartStyleSettingsModel | null = null;
    private subscribe() {
        this.base.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;
            const popupModel = this.chartStyle?.chartSettingsPopupModel;
            if (popupModel?.displayResistanceAndSupport || this.chartStyle?.primitiveMode) {
                this.removeAnnotations();
            }
        });
        this.base.chartService.subscribe_SupportResistanceLinesData((data: Array<SupportResistanceLineModel>) => {
            const popupModel = this.chartStyle?.chartSettingsPopupModel;
            if (this.isRemovingForSwitchingSymbol || this.chartStyle?.primitiveMode || popupModel?.displayResistanceAndSupport) {
                return;
            }

            this.removeAnnotations();

            this.annotations = data;
            this.create();
        });
    }

    private create() {
        if (!this.base.tiChartSeries?.chartStyleSettings || !this.base.tiChartSeries?.highStockChart?.yAxis?.length || !this.base.tiChartSeries?.highStockChart?.xAxis?.length || !this.annotations?.length) {
            return;
        }

        this.item = this.annotations[this.annotations.length - 1];
        if (!this.item) {
            return;
        }

        const fromXValue = this.base.tiChartSeries.getXValueByDateTime(this.item.from);
        const toXValue = this.item.to ? this.base.tiChartSeries.getXValueByDateTime(this.item.to) + 999 : 9999;

        this.base.tiChartSeries.highStockChart.addAnnotation({
            id: this.item.id,
            draggable: '',
            shapes: [{
                type: 'path',
                className: 'support-resistance-annotation-line',
                draggable: '',
                points: [
                    { xAxis: 0, yAxis: 0, x: fromXValue, y: this.item.price },
                    { xAxis: 0, yAxis: 0, x: toXValue, y: this.item.price }
                ]
            }]
        });

        setTimeout(() => {
            this.setPinAndPosition();
        }, 300);
    }

    private removePin() {
        this.pinElement?.remove();
        this.pinElement = null;
    }

    private setPinAndPosition() {
        const bg = this.base.element.querySelector('.highcharts-plot-background') as any;
        const path = this.base.element.querySelector('path.support-resistance-annotation-line') as any;

        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!this.item || !yAxis || !path || !bg || this.item.price < yAxis.min || this.item.price > yAxis.max) {
            this.removePin();
            return;
        }

        const bgBBox = bg.getBBox();
        const bgRect = bg.getBoundingClientRect();
        const pathBBox = path.getBBox();
        const pathRect = path.getBoundingClientRect();

        const exceedWidth = bgBBox.width - 24; // 24 is the padding for the chart area
        // Check if line is outside left/right
        if (pathBBox.x > exceedWidth) {
            this.removePin();
            return;
        }

        // Check if line is outside top/bottom
        if (pathRect.top < bgRect.top || pathRect.bottom > bgRect.bottom) {
            this.removePin();
            return;
        }

        if (!this.pinElement || !this.base.tiChartSeries.element.findFirst('.support-resistance-annotation-label-pin')) {
            this.removePin();

            this.pinElement = document.createElement('div');
            this.pinElement.className = 'support-resistance-annotation-label-pin';
            const highchartContainer = this.base.tiChartSeries.element.findFirst('.highcharts-container');
            if (highchartContainer) {
                highchartContainer.insertAdjacentElement('beforeend', this.pinElement);
            }
        }

        if (this.item?.isSupport) {
            this.pinElement.innerHTML = `<img src="${this.base.basePath}assetfiles/img/support-icon.svg" />`;
        } else {
            this.pinElement.innerHTML = `<img src="${this.base.basePath}assetfiles/img/resistance-icon.svg" />`;
        }

        const iconHeight = 9;
        const iconWidth = 19;
        let left = this.base.element.clientWidth - iconWidth;
        if (this.base.tiChartSeries?.chartStyleSettings?.showSmartRiskLevel) {
            left -= 100;
        } else {
            left -= 70;
        }

        const spacingTop = this.base.tiChartSeries?.highChartBuilder?.getChartSpacing()?.top ?? 0;
        let top = (pathRect.top - bgRect.top) + spacingTop - iconHeight;

        this.pinElement.setAttribute('style', `position: absolute; opacity: 1; top: ${top}px; left: ${left}px;`);
    }
}